// categoriesSaga.ts

import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { API } from '../../api';
import {
  assignCategories,
  assignCategoriesFailure,
  assignCategoriesSuccess,
  createCategories,
  createCategoriesFailure,
  createCategoriesSuccess,
  getCategories,
  getCategoriesFailure,
  getCategoriesSuccess,
  getCategory,
  getCategoryFailure,
  getCategorySuccess,
  removeCategories,
  removeCategoriesFailure,
  removeCategoriesSuccess,
  updateCategories,
  updateCategoriesFailure,
  updateCategoriesSuccess,
} from '../reducers/categoriesSlice';

import { errorHandler } from './common';

function* postCategory (
  studioId: number,
  token: string,
  category: any,
  parentId?: number
) {
  // @ts-ignore
  const response = yield call<any>(
    API.post,
    `/studios/${studioId}/categories`,
    {
      title: category.title,
      image: category.image,
      parent: parentId
    },
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  const categoryId = +response.data.data.id;

  if (category.services && category.services.length > 0) {
    yield all(
      category.services.map((service: any) =>
        call<any>(
          API.post,
          `/studios/${studioId}/services`,
          {
            title: service.title,
            image: service.image,
            duration: service.duration,
            priceList: service.priceList,
            categories: [categoryId]
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        )
      )
    );
  }

  if (category.children && category.children.length > 0) {
    yield all(
      category.children.map((child: any) =>
        postCategory(studioId, token, child, categoryId)
      )
    );
  }

  return response;
}


interface handleAssignCategoriesProp {
  payload: {
    studioId: number;
    categories: any[];
    onSuccess: () => void;
    onFailure: () => void;
  };
}

function* handleAssignCategoriesSaga ({
  payload: { studioId, categories, onSuccess, onFailure }
}: handleAssignCategoriesProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);

    yield all(
      categories.map((category: any) =>
        postCategory(studioId, token, category)
      )
    );

    yield put(assignCategoriesSuccess());
    onSuccess()
  } catch (error) {
    yield put(assignCategoriesFailure(error));
    onFailure()
  }
}

function* getCategoriesSaga () {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.get, '/studios/categories', {
      params: {
        limit: '500',
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    yield put(getCategoriesSuccess(response.data.data));
  } catch (error: any) {
    yield errorHandler({
      error,
      recallCallback: getCategoriesSaga,
      *callback () {
        yield put(getCategoriesFailure(error));
      },
    });
  }
}

interface getCategorySagaProps {
  payload: {
    studioId: number;
    categoryId: number;
  };
}

function* getCategorySaga ({ payload: { studioId, categoryId } }: getCategorySagaProps) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.get, `/studios/${studioId}/categories/${categoryId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    yield put(getCategorySuccess(response.data.data));
  } catch (error: any) {
    yield errorHandler({
      error,
      recallCallback: getCategorySaga,
      *callback () {
        yield put(getCategoryFailure(error));
      },
    });
  }
}

interface createCategoriesSagaProp {
  payload: {
    studioId: string;
    title: string;
    image?: string;
    parentId?: number;
    onSuccess: (id: any) => void;
    onFailure: (error: any) => void;
  };
}

function* createCategoriesSaga ({
  payload: { studioId, title, image, parentId, onSuccess, onFailure },
}: createCategoriesSagaProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(
      API.post,
      `/studios/${studioId}/categories`,
      {
        title,
        image: image && image,
        parent: parentId && parentId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    yield put(createCategoriesSuccess());
    onSuccess(response.data.data);
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield createCategoriesSaga({
          payload: { studioId, title, image, parentId, onSuccess, onFailure },
        });
      },
      *callback () {
        yield put(createCategoriesFailure(error));
        onFailure(error);
      },
    });
  }
}

interface updateCategoriesSagaProp {
  payload: {
    studioId: string;
    categoriesId: string;
    title?: string;
    image?: string;
    parentId?: number;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* updateCategoriesSaga ({
  payload: { studioId, categoriesId, title, image, parentId, onSuccess, onFailure },
}: updateCategoriesSagaProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    yield call<any>(
      API.patch,
      `studios/${studioId}/categories/${categoriesId}`,
      {
        title,
        image: image && image,
        parentId: parentId && parentId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    yield put(updateCategoriesSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield updateCategoriesSaga({
          payload: {
            studioId,
            categoriesId,
            title,
            image,
            parentId,
            onSuccess,
            onFailure,
          },
        });
      },
      *callback () {
        yield put(updateCategoriesFailure(error));
        onFailure(error);
      },
    });
  }
}

interface removeCategoriesSagaProp {
  payload: {
    studioId: number;
    categoriesId: number;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* removeCategoriesSaga ({
  payload: { studioId, categoriesId, onSuccess, onFailure },
}: removeCategoriesSagaProp) {
  try {
    // @ts-ignore
    const token = yield select(state => state.auth.token);
    // @ts-ignore
    yield call<any>(API.delete, `/studios/${studioId}/categories/${categoriesId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    yield put(removeCategoriesSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield removeCategoriesSaga({
          payload: { studioId, categoriesId, onSuccess, onFailure },
        });
        onFailure(error);
      },
      *callback () {
        yield put(removeCategoriesFailure(error));
        onFailure(error);
      },
    });
  }
}

// Watcher Saga
export function* categoriesSaga () {
  // @ts-ignore
  yield takeLatest(assignCategories().type, handleAssignCategoriesSaga);
  // @ts-ignore
  yield takeLatest(getCategories().type, getCategoriesSaga);
  //@ts-ignore
  yield takeLatest(getCategory().type, getCategorySaga);
  // @ts-ignore
  yield takeEvery(createCategories().type, createCategoriesSaga);
  // @ts-ignore
  yield takeEvery(updateCategories().type, updateCategoriesSaga);
  // @ts-ignore
  yield takeEvery(removeCategories().type, removeCategoriesSaga);
}
